import { motion } from "framer-motion";
import DotsPatternBackground from "../../assets/dots-bg.svg";
import ChalkboardFeature from "../../assets/chalkboard.avif";
import PassMapFeature from "../../assets/pass-map.avif";
import XgMapFeature from "../../assets/xg-map.avif";
import HeatMapFeature from "../../assets/heat-map.avif";
import { ReactComponent as ShapeTitle } from "../../assets/shape-title.svg";
import { useEffect, useState } from "react";

type FeatureVariant = "chalkboard" | "pass-map" | "xg-map" | "heat-map";

const FEATURE_PREVIEWS: Record<FeatureVariant, string> = {
  chalkboard: ChalkboardFeature,
  "pass-map": PassMapFeature,
  "xg-map": XgMapFeature,
  "heat-map": HeatMapFeature,
};

const MATCH_DATA_FEATURES: { title: string; variant: FeatureVariant }[] = [
  { title: "Chalkboard", variant: "chalkboard" },
  { title: "Pass Map", variant: "pass-map" },
  { title: "XG Map", variant: "xg-map" },
  { title: "Heat Map", variant: "heat-map" },
];

const FeatureTitleFlag: React.FC<{ title: string }> = ({ title }) => (
  <div className="pointer-events-none relative h-[70px] w-72 max-w-full">
    <ShapeTitle className="h-full w-full" />
    <span className="absolute inset-0 flex items-center justify-center text-xl font-extrabold text-white">
      {title}
    </span>
  </div>
);

const TypewriterText: React.FC<{
  text: string;
  className?: string;
  startDelayMs?: number;
  charIntervalMs?: number;
}> = ({ text, className = "", startDelayMs = 0, charIntervalMs = 60 }) => {
  const [displayed, setDisplayed] = useState("");

  useEffect(() => {
    let i = 0;
    let intervalId: number | null = null;
    const startTimeout = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        i += 1;
        setDisplayed(text.slice(0, i));
        if (i >= text.length && intervalId !== null) {
          window.clearInterval(intervalId);
        }
      }, charIntervalMs);
    }, startDelayMs);

    return () => {
      window.clearTimeout(startTimeout);
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [text, startDelayMs, charIntervalMs]);

  return <p className={className}>{displayed}</p>;
};

export const DataSection = () => (
  <section className="flex w-full flex-col gap-12 text-white overflow-x-hidden">
    <div className="relative isolate px-3 md:px-5 lg:px-10 2xl:px-20">
      <img
        src={DotsPatternBackground}
        alt=""
        aria-hidden
        className="hidden lg:block absolute left-1/2 top-5 h-72 w-screen -translate-x-1/2 object-cover opacity-40"
      />
      <div className="relative z-10 flex flex-col gap-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-4">
          {MATCH_DATA_FEATURES.map(({ title, variant }, index) => {
            const fromLeft = index < MATCH_DATA_FEATURES.length / 2;

            return (
              <motion.div
                key={title}
                className="flex flex-col items-center text-center"
                initial={{ x: fromLeft ? -80 : 80, opacity: 0, y: 16 }}
                whileInView={{ x: 0, opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.7,
                  ease: "easeOut",
                  delay: index * 0.05,
                }}
              >
                <div className="relative flex justify-center">
                  <FeatureTitleFlag title={title} />
                </div>
                <motion.img
                  src={FEATURE_PREVIEWS[variant]}
                  alt={`${title} preview`}
                  className="w-full object-contain -mt-1 z-10"
                  loading={index === 0 ? "eager" : "lazy"}
                  initial={{ opacity: 0, scale: 0.97, y: 10 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
    <TypewriterText
      text="and much more data..."
      className="text-center text-base lg:text-xl font-bold"
      startDelayMs={200}
      charIntervalMs={70}
    />
  </section>
);
